import { useEffect, useRef } from "react";
import { getPlatform } from "@/utils";

/**
 * useHotkey
 *
 * Register a global keyboard shortcut for the lifetime of the component.
 * `combo` is a `+`-separated string; use `mod` for the platform's primary
 * modifier (⌘ on macOS, Ctrl elsewhere).
 *
 * @example
 *   useHotkey("mod+k", () => openPalette());
 *   useHotkey("mod+b", () => toggleSidebar());
 */
export function useHotkey(
  combo: string,
  handler: (event: KeyboardEvent) => void,
  options: { enabled?: boolean; preventDefault?: boolean } = {},
): void {
  const { enabled = true, preventDefault = true } = options;
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    if (!enabled) return;

    const isMac = getPlatform() === "mac";
    const parts = combo.toLowerCase().split("+");
    const key = parts[parts.length - 1] ?? "";
    const needMod = parts.includes("mod");
    const needShift = parts.includes("shift");
    const needAlt = parts.includes("alt");

    const onKeyDown = (event: KeyboardEvent) => {
      const modActive = isMac ? event.metaKey : event.ctrlKey;
      if (needMod !== modActive) return;
      if (needShift !== event.shiftKey) return;
      if (needAlt !== event.altKey) return;
      if (event.key.toLowerCase() !== key) return;

      if (preventDefault) event.preventDefault();
      handlerRef.current(event);
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [combo, enabled, preventDefault]);
}
